/**
 * Human formatting helpers shared by the queue strip, the costs page, the
 * story wizard and the annotator status bar. Pure functions, no locale
 * plumbing: the product ships in English in v1.
 */

const usd2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
const usd3 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 3, maximumFractionDigits: 3 })

/**
 * Cost as a list-price estimate. Sub-cent amounts keep a third decimal so a
 * $0.004 haiku ping does not render as "$0.00".
 */
export function formatUsd(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  if (value === 0) return '$0.00'
  return Math.abs(value) < 0.01 ? usd3.format(value) : usd2.format(value)
}

/** Seconds → "12 s", "1 min 5 s", "2 h 3 min". Rounds to whole seconds. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—'
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s} s`
  const m = Math.floor(s / 60)
  const rem = s % 60
  if (m < 60) return rem === 0 ? `${m} min` : `${m} min ${rem} s`
  const h = Math.floor(m / 60)
  const mrem = m % 60
  return mrem === 0 ? `${h} h` : `${h} h ${mrem} min`
}

/** Milliseconds → same shape as `formatDuration`, but sub-second stays "850 ms". */
export function formatMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—'
  if (ms < 1000) return `${Math.round(ms)} ms`
  return formatDuration(ms / 1000)
}

/**
 * Percent for coordinates (0–100 space) and progress (0–1 space).
 * `formatPct(42.123)` → "42.12 %"; `formatPct(0.55, { unit: true })` → "55 %".
 */
export function formatPct(value: number | null | undefined, opts: { unit?: boolean; digits?: number } = {}): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const v = opts.unit ? value * 100 : value
  const digits = opts.digits ?? (opts.unit ? 0 : 2)
  return `${v.toFixed(digits)} %`
}

/** Bytes → "1.2 MB" (SI, one decimal above KB). */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return '—'
  if (bytes < 1000) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let v = bytes / 1000
  let i = 0
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000
    i += 1
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`
}

/** ISO timestamp → "just now", "3 min ago", "2 h ago", else a short date. */
export function formatRelative(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return '—'
  const diff = Math.max(0, now - t) / 1000
  if (diff < 45) return 'just now'
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`
  const d = new Date(t)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** Local time, including the date when it is not today (weekly pauses can span days). */
export function formatClock(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return '—'
  const date = new Date(t)
  const today = new Date(now)
  const clock = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  if (date.toDateString() === today.toDateString()) return clock
  const day = date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric',
    ...(date.getFullYear() !== today.getFullYear() ? { year: 'numeric' } as const : {}),
  })
  return `${day}, ${clock}`
}

/** Pluralise a count: `plural(1, 'place')` → "1 place", `plural(3, 'hero', 'heroes')` → "3 heroes". */
export function plural(n: number, one: string, many: string = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}

/** `job.type` → "Annotate stage", `index.full` → "Index full". */
export function humanizeJobType(type: string): string {
  const tail = type.replace(/^(ai|media|index|audio|cache|tts|export|import|annotate)\./, (m) => `${m.slice(0, -1)} `)
  const words = tail.replace(/[._]/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}
